import React, { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic.js";
import {
  Container, Box, Typography,
  IconButton, Breadcrumbs, Grid, Link as MuiLink, Tooltip,
} from '@mui/material';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import { dataConstants } from '../../../src/constants';
import _getWideScreenerMetrics from "../../../lib/screener/_getScreenerMetrics";
import getPageMetaTags from "../../../lib/meta/_getMetaTags";
import SectorTable from "../../../src/components/Sectors/SectorTable";
import useAuth from "../../../src/hook/auth";
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { downloadCSV, parseTabData } from "../../../src/utils/helpers";
import _getWideScreenerMetricsCount from "../../../lib/screener/_getScreenerMetricsCount";
import ManageColumnsDialog from "../../../src/components/Screener/ManageTableColumns.js/index.js";
import IndustriesList from "../../../src/components/Sectors/IndustriesList.js";
import getStockTabs from "../../../lib/stock/_getStockTabsData";
import getWatchlist from "../../../lib/watchlist/_getWatchlist";
import { needPremium } from "../../../src/needPremium.js";
import _getLatestPrice from "../../api/_getLatestPrice.js";
import useSWR from "swr";

const SwipeableEdgeDrawer = dynamic(() => import('../../../src/components/Sectors/SwipeableDrawer/index.js'), { ssr: false })
const IndustryHeatMap = dynamic(() => import("../../../src/components/Sectors/IndustryHeatMap/index.js"));

const convertData = (data) => {
  return data.reduce((acc, { ticker, r, p }) => {
    if (!acc[ticker]) {
      acc[ticker] = { ticker, price_data: [] };
    }
    acc[ticker].price_data.push({ price: p });
    return acc;
  }, {});
};

export const getPercent = (a, b) => {
  if (b === 0) return null;
  const percent = roundNum((a - b) * 100 / b);
  return parseFloat(percent >= 0 ? `+${percent}` : percent);
};

export const roundNum = (num) => {
  if (isNaN(num)) return num;
  return parseFloat(num.toFixed(2));
};

const getPCHG1Data = (heatmapData) => {
  const { data, error, isValidating } = useSWR(["api/_getLatestPrice", heatmapData?.map(d => d.ticker)], _getLatestPrice, {
    refreshInterval: 420000,
    revalidateOnFocus: false,
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (error.status === 404) return
      if (key === 'api/_getLatestPrice') return
      if (retryCount >= 10) return
      setTimeout(() => revalidate({ retryCount }), 420000)
    }
  }) || {}
  const { getLatestPrice: latestPrices } = data || {}
  const mergedData = React.useMemo(() => {
    if (!latestPrices) return heatmapData;

    const priceData = convertData(latestPrices);

    return heatmapData.map(item => {
      const tickerData = priceData[item.ticker];
      if (tickerData && tickerData.price_data.length >= 2) {
        const [latestPrice, previousPrice] = tickerData.price_data;
        item.PCHG = getPercent(latestPrice.price, previousPrice.price);
        item.CHG = roundNum(latestPrice.price - previousPrice.price);
        item.price = latestPrice.price;
        item.updatedAt = new Date()
      } else {
        item.PCHG = null;
        item.CHG = null;
      }
      return item;
    });
  }, [latestPrices]);

  return { data: mergedData, error, isValidating };
};

const EllipsisText = ({ text, maxLines }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (container.scrollHeight > container.clientHeight) {
      setIsOverflowing(true);
    }
  }, []);

  return (
    <Typography variant="body2" component="div" sx={{ mx: 2, mb: 1, fontWeight: 500, fontSize: '.875rem', color: 'rgb(96, 111, 123)' }}>
      <div ref={containerRef} style={{ display: '-webkit-box', WebkitLineClamp: showFullText ? 'unset' : maxLines, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {text}
      </div>
      {isOverflowing && !showFullText && <a onClick={() => setShowFullText(true)} style={{ color: "#174ea6", textDecoration: "underline", cursor: 'pointer' }}>more</a>}
      {showFullText && <a onClick={() => setShowFullText(false)} style={{ color: "#174ea6", textDecoration: "underline", cursor: 'pointer' }}>view less</a>}
    </Typography>
  );
};

const mainHeadCols = [
  { "name": "", "colSpan": "3", "includeCols": ["ticker", "shortName"] },
  { "name": "Fundamentals", "colSpan": "2", "includeCols": ["MKTCAP", "REV_TTM"] },
  { "name": "Returns", "colSpan": "2", "includeCols": ["PCHG", "CHG", "PCHG__180"] },
  { "name": "Valuation", "colSpan": "3", "includeCols": ["PE", "PS", "MKTEBT"] },
  { "name": "Growth", "colSpan": "3", "includeCols": ["ROE", "REV_TTM__PCHG_1Y", "NETINC_TTM__PCHG_1Y"] },
  { "name": "Income Statement", "colSpan": "0", "includeCols": [] },
  { "name": "Balance Sheet", "colSpan": "0", "includeCols": [] },
  { "name": "Cashflow Statement", "colSpan": "0", "includeCols": [] },
  { "name": "Statistics", "colSpan": "0", "includeCols": [] },
  { "name": "Operations", "colSpan": "0", "includeCols": [] },
  { "name": "", "colSpan": "0", "includeCols": ["delete"] }
]

const defaultCols = [
  { "label": "Fundamentals", "name": "Ticker", "tooltip": "Ticker", "field": "ticker", "urlField": "ticker", "class": "text-left", sortFunc: (a, b) => a.localeCompare(b) },
  { "label": "Fundamentals", "name": "Name", "tooltip": "Name", "field": "tickername", "class": "td-color", "urlField": "ticker" },
  { "label": "Fundamentals", "name": "Industry", "field": "industry", "class": "text-left", "isSectorValue": false, "isPct": false, "mult": 1, "tooltip": "Industry" },
  { "label": "Fundamentals", "name": "Market Cap", "field": "MKTCAP", "class": "sorted", "isSectorValue": true, "isPct": false, "mult": 1, "tooltip": "Market Capital", "isPremium": true },
  { "label": "Income Statement", "name": "Revenues TTM", "field": "REV_TTM", "class": "text-right", "isSectorValue": true, "isPct": false, "mult": 1, "tooltip": "Net sales" },
  { "label": "Returns", "name": "1D Chg", "field": "CHG", "class": "text-right", "isSectorValue": false, "isPct": false, "mult": 1, "tooltip": "1-Day Stock Price Change", "isPremium": true, width: 77 },
  { "label": "Returns", "name": "1D Pct", "field": "PCHG", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 1, "tooltip": "1-Day Stock Price Percent Change", "isPremium": true, width: 77 },
  { "label": "Returns", "name": "1W Pct", "field": "PCHG__7", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 100, "tooltip": "1-Week Stock Price Percent Change", "isPremium": true },
  { "label": "Returns", "name": "1M Pct", "field": "PCHG__30", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 100, "tooltip": "1-Month Stock Price Percent Change" },
  { "label": "Returns", "name": "1Y Pct", "field": "PCHG__365", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 100, "tooltip": "1-Year Percent Change in Stock Price" },
  { "label": "Valuation", "name": "PE", "field": "PE", "class": "text-right", "isSectorValue": false, "isPct": false, "mult": 1, "tooltip": "Price to Earnings Ratio", "isPremium": true },
  { "label": "Valuation", "name": "PS", "field": "PS", "class": "text-right", "isSectorValue": false, "isPct": false, "mult": 1, "tooltip": "Price to Sales Ratio", "isPremium": true },
  { "label": "Valuation", "name": "ROE", "field": "ROE", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 1, "tooltip": "Return on Equity" },
  { "label": "Growth", "name": "Rev 3Y Grw", "field": "REV_TTM__PCHG_3Y", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 1, "tooltip": "Reveue Grw in past 3-Years", "isPremium": true },
  { "label": "Returns", "name": "Drawdowns", "field": "DRAWDN", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 1, "tooltip": "DrawDown" }
]

const Industry = (props) => {
  const { user, logout } = useAuth()
  const { reloadUserInfo } = user || {}
  const isPremium = reloadUserInfo?.customAttributes && (JSON.parse(reloadUserInfo?.customAttributes)?.userType === "PREMIUM" || JSON.parse(reloadUserInfo?.customAttributes)?.userType === "PROFESSIONAL") ? true : false;
  const {
    _sectorMetrics, _sectorMetricsCount, screens, themeMode,
    industryName, indTabData, sectorName, showPricingBanner,
    industryHeatmapMeta,
  } = props || {}
  const { data: new_sectorMetrics, error: error2, isValidating: loadingHeatmapData } = getPCHG1Data(_sectorMetrics);
  const { getStockTabs: indTab } = indTabData || {}
  let tabData = indTab && indTab.length > 0 ? indTab.find(d => d.tab_code === "INDUSTRY") : null;
  tabData = tabData ? parseTabData(tabData) : null
  const { cols, popularIndustries } = tabData?.settings || {}
  const [sectorMetrics, setSectorMetrics] = React.useState(new_sectorMetrics && new_sectorMetrics.length > 0 ? new_sectorMetrics : _sectorMetrics);
  const [estimatedResults, setEstimatedResults] = React.useState(_sectorMetricsCount ?? null);
  const [showingResults, setShowingResults] = React.useState(new_sectorMetrics?.length);
  const [isTableLoading, setTableLoading] = React.useState(false);
  const [first, setFirst] = useState(100);
  const [offset, setOffset] = useState(0);
  const [tableCols, setTableCols] = React.useState(cols ?? defaultCols);
  const [banner, setBanner] = React.useState(false);
  const [columnGroups, setColumnGroups] = useState([])
  const [watchlists, setWatchlists] = React.useState([]);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setTableCols(prevValue => {
      const newValue = [...prevValue];
      const [itemToMove] = newValue.splice(oldIndex, 1);
      newValue.splice(newIndex, 0, itemToMove);
      return newValue;
    });
  };

  const handleDownload = (e) => {
    if (isPremium) {
      downloadCSV(
        sectorMetrics?.sort((a, b) => {
          return b?.MKTCAP - a?.MKTCAP;
        }),
        tableCols,
        `${industryName} Table`,
        true
      );
    } else {
      showPricingBanner(e, industryName)
    }
  };

  const getScreenersCount = async (andor, formula) => {
    if (formula?.length > 0) {
      await _getWideScreenerMetricsCount(andor, formula).then((d) => {
        setEstimatedResults(d?.getWideScreenerMetricsCountV3)
      }).catch((error) => {
        console.log(error);
      })
    }
  }

  const getScreeners = async (andor, formula) => {
    if (formula?.length > 0) {
      setTableLoading(true);
      await _getWideScreenerMetrics(andor, formula, first, offset).then((d) => {
        setShowingResults(showingResults + d?.getWideScreenerMetricsV3?.length);
        offset > 0 ?
          setSectorMetrics(d?.message?.message ? { err: d?.message } : [...sectorMetrics, ...d?.getWideScreenerMetricsV3]) :
          setSectorMetrics(d?.message?.message ? { err: d?.message } : d?.getWideScreenerMetricsV3);
        setTableLoading(false);
      }).catch((error) => {
        setTableLoading(false);
      });
    }
  };

  useEffect(() => {
    if (offset > 0) {
      getScreeners('AND', [{ andor: "AND", metricsCriteria: [{ code: `\`industry\``, condition: "EQUALS", value: `${industryName}` }] }])
      getScreenersCount('AND', [{ andor: "AND", metricsCriteria: [{ code: `\`industry\``, condition: "EQUALS", value: `${industryName}` }] }])
    }
  }, [first, offset])

  const _get_watchlist_ = async (accessToken) => {
    if (accessToken) {
      await getWatchlist(accessToken).then(data => {
        const { getWatchlist } = data || {}
        return setWatchlists(getWatchlist ?? [])
      });
    }
  }
  useEffect(() => {
    _get_watchlist_(user?.accessToken);
  }, [user?.accessToken])

  const foundIndustry = dataConstants?.INDUSTRY?.find(d => d.shortName === industryName) ?? {}

  return (
    <Container maxWidth="xl" sx={{ pb: 6 }}>
      {/* Breadcrumb */}
      <Box component="nav" sx={{ pt: { xs: 1.5, sm: 2 }, pb: 0.5 }}>
        <Breadcrumbs
          separator={<NavigateNextRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
          aria-label="breadcrumb"
        >
          <MuiLink
            underline="hover"
            href="/"
            sx={{ color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1, '&:hover': { color: 'primary.main' } }}
          >
            Home
          </MuiLink>
          <MuiLink
            underline="hover"
            href="/sectors"
            sx={{ color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1, '&:hover': { color: 'primary.main' } }}
          >
            Sectors
          </MuiLink>
          <Typography sx={{ color: 'text.primary', fontSize: '0.8rem', fontWeight: 500, lineHeight: 1 }}>
            {industryName}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Page Header */}
      <Box sx={{ pt: 1, pb: { xs: 2, sm: 2.5 } }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            fontWeight: 700,
            color: 'text.primary',
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
          }}
        >
          {industryName}
        </Typography>
        {sectorName && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              fontSize: '0.9rem',
              color: 'rgb(100, 116, 139)',
              lineHeight: 1.5,
            }}
          >
            Sector: {sectorName}
          </Typography>
        )}
      </Box>

      {/* Mobile Industry Drawer */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <SwipeableEdgeDrawer
          text="View all Industries"
          industryName={industryName}
          popularIndustries={popularIndustries}
          subTab={true}
        />
      </Box>

      {/* Main Content Grid */}
      <Grid container columnSpacing={2.5} sx={{ alignItems: 'start' }}>
        <IndustriesList
          industryName={industryName}
          popularIndustries={popularIndustries}
          subTab={true}
          sx={{ display: { xs: 'none', sm: 'grid', md: 'grid' } }}
        />
        <Grid size={{ xs: 12, sm: 8.5, md: 10 }} sx={{ mt: { xs: 1, sm: 0 } }}>
          <IndustryHeatMap
            data={new_sectorMetrics}
            isLoading={loadingHeatmapData}
            industry={foundIndustry?.shortName}
            heatmapColorSettings={industryHeatmapMeta ? industryHeatmapMeta.settings : null}
          />

          {/* Table Card */}
          <Box sx={{
            mt: 2.5,
            borderRadius: '12px',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
          }}>
            <SectorTable
              rows={sectorMetrics ?? []}
              headCells={tableCols}
              setTableCols={setTableCols}
              defaultHeadCells={tableCols}
              wrapperClass={'sector-table'}
              shortWrapperClass={'sector-field'}
              quickSearch={true}
              needToolbar={true}
              fullToolbarAccess={false}
              createSections={false}
              noOfRows={25}
              estimatedResults={estimatedResults ?? 0}
              isLoading={isTableLoading}
              setOffset={setOffset}
              offset={offset}
              mounted={false}
              isPremium={isPremium}
              user={user}
              setBanner={setBanner}
              _watchlists={watchlists}
              manageColumns={
                <ManageColumnsDialog
                  arrayOrder={mainHeadCols}
                  columns={tableCols}
                  setColumnGroups={setColumnGroups}
                  setColumns={setTableCols}
                  onSortEnd={onSortEnd}
                />}
              download={
                <Tooltip title="Download as CSV" placement="top" disableInteractive>
                  <IconButton
                    onClick={handleDownload}
                    data-event-name="download"
                    data-event-category={`industry: ${foundIndustry?.shortName}`}
                    data-event-label="csv"
                    disabled={sectorMetrics?.length === 0}
                    sx={{ mr: 'auto' }}
                  >
                    <FileDownloadRoundedIcon fontSize="small" sx={{ color: sectorMetrics?.length === 0 ? 'rgba(0, 0, 0, 0.26)' : 'text.secondary' }} />
                  </IconButton>
                </Tooltip>}
              tableName={foundIndustry?.shortName &&
                <Box component='div' sx={{ pt: 0, mr: 'auto' }}>
                  {foundIndustry?.shortName &&
                    <Typography component="div" sx={{ mx: 2, fontWeight: 600, fontSize: '1rem', color: 'text.primary', lineHeight: 1.6 }}>
                      {foundIndustry?.shortName}
                    </Typography>}
                  {foundIndustry?.description &&
                    <EllipsisText text={foundIndustry?.description} maxLines={2} />}
                </Box>}
              results={showingResults ?
                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, ml: 2, mr: 'auto', color: 'rgb(100, 116, 139)', display: { xs: 'none', sm: 'block' } }}>
                  {estimatedResults} result{estimatedResults <= 1 ? '' : 's'} found
                </Typography>
                : ''}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

export async function getStaticPaths() {
  return {
    paths: dataConstants.INDUSTRY.map(industry => ({
      params: { industry: industry.shortName?.replace(/&/g, 'and').replace(/\s/g, '-')?.toLowerCase() },
    })),
    fallback: 'blocking'
  }
}

export async function getStaticProps({ params, tabCode = "INDUSTRY", preview = false, type = "industry", _ = '' }) {
  const industry = params?.industry
    ?.replace(/\b\w/g, (char) => char.toUpperCase())
    ?.replace(/reit-/i, 'REIT ')
    ?.replace(/-eandp/i, ' E&P')
    ?.replace(/non-alcoholic/i, 'Non\u2013Alcoholic')
    ?.replace(/-And-/g, ' & ')
    ?.replace(/ And /g, ' & ')
    ?.replace(/-/g, ' ')
    ?.replace(/\u2013/g, '-');

  const andor = 'AND';
  const formula = [{
    andor: 'AND',
    metricsCriteria: [{
      code: `\`industry\``,
      condition: 'EQUALS',
      value: industry,
    }]
  }];
  const first = 100; const offset = 0;
  const industry_tab_data = (await getStockTabs(undefined, tabCode, preview)) || [];
  const metrics = await _getWideScreenerMetrics(andor, formula, first, offset) || null;
  const metricsListCount = await _getWideScreenerMetricsCount(andor, formula) || null;
  const sector_tab_data = (await getStockTabs("AAPL", "SECTOR", "COLR", null, null, null)) || [];
  const { getStockTabs: secTab } = sector_tab_data || {}
  let heatmapColors = secTab && secTab.length > 0 ? secTab.find(d => d.section_code === "heatmap") : null;
  const metaTags = await getPageMetaTags('/sectors', false) || {};
  const meta_tags = (await getPageMetaTags(`/sectors/industry/${params?.industry}`, false)) || {}
  const { getMetaTags } = meta_tags || {}
  const found = dataConstants.INDUSTRY?.find(d => d.shortName === industry);

  return {
    props: {
      indTabData: industry_tab_data,
      meta_tags: getMetaTags?.tags || null,
      industryName: industry,
      sectorName: found?.sector,
      _sectorMetrics: metrics?.getWideScreenerMetricsV3 || null,
      _sectorMetricsCount: metricsListCount?.getWideScreenerMetricsCountV3 || null,
      industryHeatmapMeta: heatmapColors || {}
    },
    revalidate: dataConstants.REVALIDATE_IN.DEFAULT_HOURS,
    notFound: found ? false : true,
  }
}

export default needPremium(Industry);
